/**
 * The black box, on screen.
 *
 * It already rides along in every bug report, and that was the plan right up
 * until the reports stopped arriving: they sync to a collector on the PC over
 * the tailnet, and a report written at two in the morning on a boat sits queued
 * on the phone until the PC is running and reachable. Which is exactly when the
 * answer is wanted and exactly when it is furthest away.
 *
 * So it is readable here too, on the phone, with nothing else switched on. Open
 * the menu, read it out, done. Copy puts the lot on the clipboard for pasting
 * into a message.
 *
 * This is a diagnostics panel and it is allowed to look like one: fixed-width,
 * dense, no explaining away. Someone standing here is trying to find out what
 * happened, not to be reassured.
 */
import { useState, useEffect } from 'react';
import { useShellStore } from '@/store/shell';
import { haptic } from '@/ui/haptics';
import { APP_VERSION, APP_COMMIT } from '@/update/appVersion';
import { lastRunTail, lastRunVerdict, liveTail } from './HeartbeatRunner';
import { rendererDeathSummary, rendererDeaths } from './rendererDeaths';
import { freezeLine } from './HeartbeatRunner';
import { canvasHealthSummary } from '@/map/canvasHealth';
import { nativeAnchorStatus, type NativeAnchorStatus } from '@/safety/anchorNative';

/** What each verdict means, in words, since the word alone is not enough. */
function verdictNote(kind: string): string {
  if (kind === 'starved') {
    return 'The last run was getting slower and slower before it stopped. That is the app running out of room, and the heap figure below says whether it was memory.';
  }
  if (kind === 'stalled') {
    return 'The last run was keeping up right until it stopped dead. Nothing was struggling, so something took it away from outside: Android reclaiming us, or the renderer going.';
  }
  if (kind === 'clean') {
    return 'The last run was still ticking when it ended. Nothing to explain.';
  }
  return 'No previous run recorded yet. This is normal on a fresh install, or the first run after an update.';
}

/** "42 s ago", or "never" when the native watch has not seen a fix yet. */
function nativeAge(ms: number | undefined): string {
  if (ms == null || ms < 0) return 'never';
  const s = Math.round(ms / 1000);
  return s < 120 ? `${s} s ago` : `${Math.round(s / 60)} min ago`;
}

export function DiagnosticsPanel() {
  const [live, setLive] = useState(false);
  const verdict = lastRunVerdict();
  const tail = lastRunTail();
  const deaths = rendererDeaths();
  const deathLine = rendererDeathSummary();
  const canvasLine = canvasHealthSummary();
  // The native watch keeps its own pulse, in its own process. Read alongside the
  // JavaScript pulses above it settles the question the black box could not
  // answer on its own: pulses stopping while the native watch carried on means
  // the page was frozen or the renderer went, and both stopping together means
  // the whole app was killed. Those want completely different fixes and they
  // look identical from inside the web app.
  const [native, setNative] = useState<NativeAnchorStatus | null>(null);
  useEffect(() => {
    let live = true;
    void nativeAnchorStatus().then((s) => {
      if (live) setNative(s);
    });
    return () => {
      live = false;
    };
  }, []);
  const nativeLine = !native
    ? 'native anchor watch: not available (web build)'
    : native.armed
      ? `native anchor watch ARMED, last checked a fix ${nativeAge(native.lastCheckAgeMs)}`
      : 'native anchor watch: nothing armed';

  const report = [
    `build ${APP_VERSION.versionName} (${APP_VERSION.versionCode}) commit ${APP_COMMIT}`,
    verdict.summary,
    freezeLine() ?? 'no background freezes recorded',
    deathLine ?? 'no renderer deaths recorded',
    canvasLine ?? 'chart canvas has matched the screen throughout',
    nativeLine,
    ...(native?.log ? ['--- native anchor watch ---', ...native.log.trim().split('\n')] : []),
    '--- last run, final pulses ---',
    ...tail,
    ...(deaths.length ? ['--- renderer deaths ---', ...deaths] : []),
  ].join('\n');

  return (
    <div className="acp-chartsrc">
      <span className="lead">
        What the app knows about how it last stopped. It writes itself a pulse every ten seconds and keeps it
        across a kill, so a freeze can be read afterwards rather than guessed at.
      </span>

      <label className="acp-fieldlabel">This build</label>
      <p className="acp-menu-note" style={{ fontFamily: 'monospace', fontSize: 12 }}>
        {APP_VERSION.versionName} ({APP_VERSION.versionCode}) commit {APP_COMMIT}
      </p>

      <label className="acp-fieldlabel">Previous run</label>
      <p className="acp-menu-note" style={{ fontFamily: 'monospace', fontSize: 12 }}>
        {verdict.summary}
      </p>
      <p className="acp-menu-note">{verdictNote(verdict.kind)}</p>

      <label className="acp-fieldlabel">Renderer</label>
      <p className="acp-menu-note">
        {deathLine ??
          'Android has never taken the chart renderer from us. That is the healthy answer: when it does, the app relaunches itself and notes it here.'}
      </p>

      <label className="acp-fieldlabel">
        {live ? 'This run, last few pulses' : 'Last run, final pulses'}
      </label>
      <pre
        style={{
          fontFamily: 'monospace',
          fontSize: 11,
          lineHeight: 1.5,
          overflowX: 'auto',
          margin: '4px 0',
          whiteSpace: 'pre',
        }}
      >
        {(live ? liveTail() : tail).join('\n') || '(nothing recorded)'}
      </pre>
      <p className="acp-menu-note">
        lag is how late each pulse was against when it was due, so a big lag is the app failing to answer its
        own timer. bg means it was off screen. heap is megabytes in use, trk is points in the past track.
      </p>

      <div className="picks compact">
        <button
          type="button"
          className={live ? 'acp-btn on' : 'acp-btn'}
          onClick={() => { haptic('tick'); setLive(!live); }}
        >
          {live ? 'Show last run' : 'Show this run'}
        </button>
        <button
          type="button"
          className="acp-btn"
          onClick={() => {
            haptic('commit');
            void navigator.clipboard
              ?.writeText(report)
              .then(() => useShellStore.getState().showToast('Diagnostics copied'))
              .catch(() => useShellStore.getState().showToast('Could not copy — read it off the screen'));
          }}
        >
          Copy
        </button>
      </div>
    </div>
  );
}
